<script setup lang="ts">
import { ComboboxItem } from 'reka-ui';
import { computed, toRef } from 'vue';

import type { VueClass } from '@/kasten-components/types';
import { cnMerge } from '@/utils/utils.js';
import IconPlus from '~icons/lucide/plus';

import { comboboxCreateOptionVariants } from './combobox-variants';
import { type KaComboboxOptionProps } from './KaComboboxOption.vue';

export type KaComboboxCreateOptionProps = {
  searchTerm?: string;
  /**
   * Label template for the create option. Use `$searchTerm` as a placeholder
   * for the typed value. Example: `'Reference "$searchTerm"'`
   * @default 'Create "$searchTerm"'
   */
  createLabel?: string;
  /**
   * Label shown when there is no search term (e.g. an always-present create row
   * before the user types). The empty state never falls back to `createLabel`,
   * so a template like `Create "$searchTerm"` cannot degrade to `Create ""`.
   * @default 'Create new…'
   */
  createLabelEmpty?: string;
};

const props = withDefaults(defineProps<KaComboboxCreateOptionProps>(), {
  searchTerm: '',
  createLabel: 'Create "$searchTerm"',
  createLabelEmpty: 'Create new…',
});

const emit = defineEmits<{
  (e: 'create', option: string): void;
}>();

defineSlots<{
  default?(props: { option: string }): any;
}>();

const searchTerm = toRef(props, 'searchTerm');
const displayLabel = computed(() =>
  props.searchTerm
    ? props.createLabel.replace('$searchTerm', props.searchTerm)
    : props.createLabelEmpty,
);
const optionToAdd = computed<KaComboboxOptionProps>(() => ({
  label: searchTerm.value || '',
  value: searchTerm.value || '',
  textValue: searchTerm.value || '',
}));
</script>
<template>
  <!-- Native <button> to match the option rows; type="button" so this clickable row can't submit
       a surrounding <form> (reka's Primitive sets no type). appearance-none + text-left undo the
       button's default chrome/centering. -->
  <ComboboxItem
    as="button"
    type="button"
    :name="searchTerm"
    :value="optionToAdd"
    :class="
      cnMerge(comboboxCreateOptionVariants(), 'appearance-none text-left', $attrs.class as VueClass)
    "
    @select.prevent="emit('create', searchTerm)"
  >
    <span class="flex items-center gap-2">
      <IconPlus class="size-4 flex-shrink-0" aria-hidden="true" />
      <span :class="['block', { truncate: searchTerm }]" :title="searchTerm">
        <slot :option="searchTerm">{{ displayLabel }}</slot>
      </span>
    </span>
  </ComboboxItem>
</template>
